import { useCallback, useState } from "react";

import { FileRecord } from "@/shared/domain/files";

/**
 * Discriminated union for active source
 */
export type ActiveSource = { type: "dataset"; id: string } | null;

/**
 * Map of sourceId -> Map of fileId -> FileRecord
 *
 * Source ID is unique across source types since its prefixed by type.
 * e.g. "ds_abc123"
 */
type SelectedFiles = Map<string, Map<string, FileRecord>>;

/**
 * Hook to manage file picker state
 */
export function useFilePicker(existingFileIds?: string[]) {
  const [activeSource, setActiveSource] = useState<ActiveSource | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFiles>(new Map());

  const clearAllSelections = useCallback(function clearAllSelections(): void {
    setSelectedFiles(new Map());
  }, []);

  const resetState = useCallback(function resetState(): void {
    setActiveSource(null);
    setSelectedFiles(new Map());
  }, []);

  const isFileSelected = useCallback(
    function isFileSelected(fileId: string): boolean {
      if (activeSource === null) {
        return false;
      }

      const sourceId = activeSource.id;
      const sourceFileMap = selectedFiles.get(sourceId);
      return sourceFileMap?.has(fileId) ?? false;
    },
    [activeSource, selectedFiles],
  );

  const isFileAlreadyAdded = useCallback(
    function isFileAlreadyAdded(fileId: string): boolean {
      return existingFileIds?.includes(fileId) ?? false;
    },
    [existingFileIds],
  );

  const getSelectedFiles = useCallback(
    function getSelectedFiles(): FileRecord[] {
      const allFiles: FileRecord[] = [];

      for (const fileMap of selectedFiles.values()) {
        allFiles.push(...fileMap.values());
      }

      return allFiles;
    },
    [selectedFiles],
  );

  const getSelectedCountForDataset = useCallback(
    function getSelectedCountForDataset(datasetId: string): number {
      const sourceFileMap = selectedFiles.get(datasetId);
      return sourceFileMap?.size ?? 0;
    },
    [selectedFiles],
  );

  const toggleFileSelection = useCallback(
    function toggleFileSelection(file: FileRecord): void {
      if (!activeSource) {
        return;
      }
      setSelectedFiles((prev) => {
        const sourceId = activeSource.id;
        const newSelectedFiles = new Map(prev);

        // Get or create the file map for this source
        let sourceFileMap = newSelectedFiles.get(sourceId);
        if (!sourceFileMap) {
          sourceFileMap = new Map();
          newSelectedFiles.set(sourceId, sourceFileMap);
        } else {
          // Clone the inner map to avoid mutation
          sourceFileMap = new Map(sourceFileMap);
          newSelectedFiles.set(sourceId, sourceFileMap);
        }

        // Toggle the file
        if (sourceFileMap.has(file.file_id)) {
          sourceFileMap.delete(file.file_id);
          // Clean up empty source maps
          if (sourceFileMap.size === 0) {
            newSelectedFiles.delete(sourceId);
          }
        } else {
          sourceFileMap.set(file.file_id, file);
        }

        return newSelectedFiles;
      });
    },
    [activeSource],
  );

  return {
    // State
    activeSource,
    numFilesSelected: getSelectedFileCount(selectedFiles),

    // Actions
    clearAllSelections,
    isFileSelected,
    isFileAlreadyAdded,
    getSelectedFiles,
    getSelectedFileCount,
    getSelectedCountForDataset,
    resetState,
    setActiveSource,
    toggleFileSelection,
  };
}

function getSelectedFileCount(selectedFiles: SelectedFiles): number {
  let count = 0;
  for (const fileMap of selectedFiles.values()) {
    count += fileMap.size;
  }
  return count;
}
